/**
 * Every shape the editor can draw, in one place. §6.4.
 *
 * A shape is a function from a box to SVG path data in millimetres. That is
 * the whole abstraction, and it is deliberately narrow, because the alternative
 * is what this file replaced: a switch in the canvas and another in the press
 * walk, drifting apart one shape at a time until a star on screen is a
 * different star on the press.
 *
 * SVG path data because it is what the rest of the project already speaks — a
 * font hands back path data, the SVG sink writes it out unchanged, and the PDF
 * sink converts it once, in `pathOps`. Adding a shape here adds it everywhere,
 * including the proof and the press file, with nothing else to remember.
 *
 * Two rules for anything added below:
 *
 *   Fill the box. A shape is drawn from 0,0 to w,h and should use all of it,
 *   so that dragging the handles does what it looks like it will do.
 *
 *   Curves are cubic. PDF has no quadratic and no arc, so a shape written with
 *   one would be converted approximately rather than exactly. `arc` and `k`
 *   below give the cubic forms.
 */

import type { PathNode, ShapeLayer } from '../model/types';

/** Three decimal places is a micron. Nothing in print needs the fourth. */
const mm = ( value: number ) => Math.round( value * 1000 ) / 1000;

/**
 * The magic number for drawing a quarter circle with one cubic.
 *
 * 4/3 × (√2 − 1). Every rounded corner and every ellipse in this file is built
 * from it.
 */
const K = 0.5522847498307936;

export interface ShapeDef {
  id: ShapeLayer['shape'];
  name: string;
  group: 'Basic' | 'Polygons' | 'Stars and badges' | 'Arrows and lines' | 'Callouts' | 'Decorative';
  /**
   * Whether the shape reads `radius` (a corner) or `points` (sides or spikes),
   * so the properties panel can offer the control that means something and hide
   * the one that does not.
   */
  adjust?: 'radius' | 'points';
  /** Drawn as an outline rather than filled: a line has no inside. */
  strokeOnly?: boolean;
  /** Made with the pen rather than chosen from the gallery. */
  drawn?: boolean;
  /**
   * How wide this shape wants to be for its height.
   *
   * A ribbon is a banner and a line is a line; both look wrong in a square and
   * arrive wrong if placed in one. Read by the picker for its previews and by
   * the insert, so what the customer clicks is the proportion they get.
   */
  ratio?: number;
  path( w: number, h: number, layer: Adjustable ): string;
}

/** Only the fields a path generator is allowed to read. */
export interface Adjustable {
  radius: number;
  points: number;
  /** The points of a drawn path, for the one shape that has geometry of its own. */
  nodes?: PathNode[];
  closed?: boolean;
}

/* ---------------------------------------------------------------
 * Path building
 * ------------------------------------------------------------ */

const M = ( x: number, y: number ) => `M ${ mm( x ) } ${ mm( y ) }`;
const L = ( x: number, y: number ) => `L ${ mm( x ) } ${ mm( y ) }`;

const C = ( x1: number, y1: number, x2: number, y2: number, x: number, y: number ) =>
  `C ${ mm( x1 ) } ${ mm( y1 ) } ${ mm( x2 ) } ${ mm( y2 ) } ${ mm( x ) } ${ mm( y ) }`;

/** A full ellipse, as the four cubics PDF needs. */
function ellipse( cx: number, cy: number, rx: number, ry: number ): string {
  const ox = rx * K;
  const oy = ry * K;

  return [
    M( cx, cy - ry ),
    C( cx + ox, cy - ry, cx + rx, cy - oy, cx + rx, cy ),
    C( cx + rx, cy + oy, cx + ox, cy + ry, cx, cy + ry ),
    C( cx - ox, cy + ry, cx - rx, cy + oy, cx - rx, cy ),
    C( cx - rx, cy - oy, cx - ox, cy - ry, cx, cy - ry ),
    'Z',
  ].join( ' ' );
}

/** A rectangle, with corners rounded by however much fits. */
function rounded( w: number, h: number, r: number ): string {
  const radius = Math.max( 0, Math.min( r, w / 2, h / 2 ) );

  if ( radius === 0 ) {
    return [ M( 0, 0 ), L( w, 0 ), L( w, h ), L( 0, h ), 'Z' ].join( ' ' );
  }

  const k = radius * K;

  return [
    M( radius, 0 ),
    L( w - radius, 0 ),
    C( w - radius + k, 0, w, radius - k, w, radius ),
    L( w, h - radius ),
    C( w, h - radius + k, w - radius + k, h, w - radius, h ),
    L( radius, h ),
    C( radius - k, h, 0, h - radius + k, 0, h - radius ),
    L( 0, radius ),
    C( 0, radius - k, radius - k, 0, radius, 0 ),
    'Z',
  ].join( ' ' );
}

/** A ring of points, closed. The workhorse behind every polygon and star. */
function ring( points: Array<[ number, number ]> ): string {
  return points
    .map( ( [ x, y ], i ) => ( i === 0 ? M( x, y ) : L( x, y ) ) )
    .concat( 'Z' )
    .join( ' ' );
}

/**
 * A regular figure inscribed in the box, starting at the top.
 *
 * @param spikes When given, every other point is pulled in to this fraction of
 *               the radius, which is what makes a star a star.
 */
function regular( w: number, h: number, sides: number, spikes?: number ): string {
  const cx = w / 2;
  const cy = h / 2;
  const count = Math.max( 3, Math.round( sides ) ) * ( spikes ? 2 : 1 );
  const out: Array<[ number, number ]> = [];

  for ( let i = 0; i < count; i++ ) {
    // From the top, clockwise, as every drawing tool does.
    const angle = ( i / count ) * Math.PI * 2 - Math.PI / 2;
    const pull = spikes && i % 2 ? spikes : 1;

    out.push( [ cx + Math.cos( angle ) * cx * pull, cy + Math.sin( angle ) * cy * pull ] );
  }

  return ring( out );
}

/* ---------------------------------------------------------------
 * The catalogue
 * ------------------------------------------------------------ */

export const SHAPES: ShapeDef[] = [
  /* --- Basic ------------------------------------------------ */
  {
    id: 'rect',
    name: 'Rectangle',
    group: 'Basic',
    adjust: 'radius',
    path: ( w, h, l ) => rounded( w, h, l.radius ),
  },
  {
    id: 'ellipse',
    name: 'Ellipse',
    group: 'Basic',
    path: ( w, h ) => ellipse( w / 2, h / 2, w / 2, h / 2 ),
  },
  {
    id: 'triangle',
    name: 'Triangle',
    group: 'Basic',
    path: ( w, h ) => ring( [ [ w / 2, 0 ], [ w, h ], [ 0, h ] ] ),
  },
  {
    id: 'right-triangle',
    name: 'Right triangle',
    group: 'Basic',
    path: ( w, h ) => ring( [ [ 0, 0 ], [ w, h ], [ 0, h ] ] ),
  },
  {
    id: 'diamond',
    name: 'Diamond',
    group: 'Basic',
    path: ( w, h ) => ring( [ [ w / 2, 0 ], [ w, h / 2 ], [ w / 2, h ], [ 0, h / 2 ] ] ),
  },
  {
    id: 'parallelogram',
    ratio: 1.6,
    name: 'Parallelogram',
    group: 'Basic',
    path: ( w, h ) => ring( [ [ w * 0.25, 0 ], [ w, 0 ], [ w * 0.75, h ], [ 0, h ] ] ),
  },
  {
    id: 'trapezium',
    ratio: 1.4,
    name: 'Trapezium',
    group: 'Basic',
    path: ( w, h ) => ring( [ [ w * 0.22, 0 ], [ w * 0.78, 0 ], [ w, h ], [ 0, h ] ] ),
  },
  {
    id: 'arch',
    name: 'Arch',
    group: 'Basic',
    path: ( w, h ) => {
      const r = w / 2;
      const k = r * K;

      return [
        M( 0, h ),
        L( 0, r ),
        C( 0, r - k, r - k, 0, r, 0 ),
        C( r + k, 0, w, r - k, w, r ),
        L( w, h ),
        'Z',
      ].join( ' ' );
    },
  },

  /* --- Polygons --------------------------------------------- */
  {
    id: 'pentagon',
    name: 'Pentagon',
    group: 'Polygons',
    path: ( w, h ) => regular( w, h, 5 ),
  },
  {
    id: 'hexagon',
    name: 'Hexagon',
    group: 'Polygons',
    path: ( w, h ) => regular( w, h, 6 ),
  },
  {
    id: 'octagon',
    name: 'Octagon',
    group: 'Polygons',
    path: ( w, h ) => regular( w, h, 8 ),
  },
  {
    id: 'polygon',
    name: 'Polygon',
    group: 'Polygons',
    adjust: 'points',
    path: ( w, h, l ) => regular( w, h, l.points || 6 ),
  },

  /* --- Stars and badges ------------------------------------- */
  {
    id: 'star',
    name: 'Star',
    group: 'Stars and badges',
    adjust: 'points',
    path: ( w, h, l ) => regular( w, h, l.points || 5, 0.5 ),
  },
  {
    id: 'burst',
    name: 'Burst',
    group: 'Stars and badges',
    adjust: 'points',
    // Shallower spikes and more of them: the SALE flash, not a rating star.
    path: ( w, h, l ) => regular( w, h, l.points || 12, 0.78 ),
  },
  {
    id: 'seal',
    name: 'Seal',
    group: 'Stars and badges',
    adjust: 'points',
    path: ( w, h, l ) => {
      // A scalloped edge: a bump on every segment rather than a spike.
      const count = Math.max( 6, Math.round( l.points || 12 ) );
      const cx = w / 2;
      const cy = h / 2;
      const out: string[] = [];

      for ( let i = 0; i < count; i++ ) {
        const a = ( i / count ) * Math.PI * 2 - Math.PI / 2;
        const b = ( ( i + 1 ) / count ) * Math.PI * 2 - Math.PI / 2;
        const mid = ( a + b ) / 2;
        // The control points sit outside the circle, which is what lifts the
        // segment into a bump rather than leaving it a flat chord.
        const bulge = 1.28;

        const px = cx + Math.cos( a ) * cx;
        const py = cy + Math.sin( a ) * cy;
        const qx = cx + Math.cos( b ) * cx;
        const qy = cy + Math.sin( b ) * cy;
        const mx = cx + Math.cos( mid ) * cx * bulge;
        const my = cy + Math.sin( mid ) * cy * bulge;

        if ( i === 0 ) out.push( M( px, py ) );

        // One quadratic raised to a cubic, so PDF takes it exactly.
        out.push( C(
          px + ( 2 / 3 ) * ( mx - px ), py + ( 2 / 3 ) * ( my - py ),
          qx + ( 2 / 3 ) * ( mx - qx ), qy + ( 2 / 3 ) * ( my - qy ),
          qx, qy
        ) );
      }

      out.push( 'Z' );

      return out.join( ' ' );
    },
  },
  {
    id: 'shield',
    name: 'Shield',
    group: 'Stars and badges',
    path: ( w, h ) => [
      M( 0, 0 ),
      L( w, 0 ),
      L( w, h * 0.55 ),
      C( w, h * 0.8, w * 0.75, h * 0.94, w / 2, h ),
      C( w * 0.25, h * 0.94, 0, h * 0.8, 0, h * 0.55 ),
      'Z',
    ].join( ' ' ),
  },
  {
    id: 'tag',
    ratio: 2,
    name: 'Tag',
    group: 'Stars and badges',
    path: ( w, h ) => {
      const notch = Math.min( w * 0.18, h / 2 );

      return ring( [
        [ notch, 0 ], [ w, 0 ], [ w, h ], [ notch, h ], [ 0, h / 2 ],
      ] );
    },
  },

  /* --- Arrows and lines ------------------------------------- */
  {
    id: 'line',
    ratio: 6,
    name: 'Line',
    group: 'Arrows and lines',
    strokeOnly: true,
    path: ( w, h ) => `${ M( 0, h / 2 ) } ${ L( w, h / 2 ) }`,
  },
  {
    id: 'arrow',
    ratio: 3,
    name: 'Arrow',
    group: 'Arrows and lines',
    path: ( w, h ) => {
      const head = Math.min( w / 3, h );
      const shaft = h / 3;

      return ring( [
        [ 0, h / 2 - shaft / 2 ],
        [ w - head, h / 2 - shaft / 2 ],
        [ w - head, 0 ],
        [ w, h / 2 ],
        [ w - head, h ],
        [ w - head, h / 2 + shaft / 2 ],
        [ 0, h / 2 + shaft / 2 ],
      ] );
    },
  },
  {
    id: 'double-arrow',
    ratio: 3,
    name: 'Double arrow',
    group: 'Arrows and lines',
    path: ( w, h ) => {
      const head = Math.min( w / 4, h );
      const shaft = h / 3;

      return ring( [
        [ 0, h / 2 ],
        [ head, 0 ],
        [ head, h / 2 - shaft / 2 ],
        [ w - head, h / 2 - shaft / 2 ],
        [ w - head, 0 ],
        [ w, h / 2 ],
        [ w - head, h ],
        [ w - head, h / 2 + shaft / 2 ],
        [ head, h / 2 + shaft / 2 ],
        [ head, h ],
      ] );
    },
  },
  {
    id: 'block-arrow',
    ratio: 2,
    name: 'Block arrow',
    group: 'Arrows and lines',
    path: ( w, h ) => {
      const head = Math.min( w / 2, w * 0.4 );

      return ring( [
        [ 0, h * 0.25 ],
        [ w - head, h * 0.25 ],
        [ w - head, 0 ],
        [ w, h / 2 ],
        [ w - head, h ],
        [ w - head, h * 0.75 ],
        [ 0, h * 0.75 ],
      ] );
    },
  },
  {
    id: 'chevron',
    ratio: 2.4,
    name: 'Chevron',
    group: 'Arrows and lines',
    path: ( w, h ) => {
      const point = Math.min( w * 0.25, h / 2 );

      return ring( [
        [ 0, 0 ],
        [ w - point, 0 ],
        [ w, h / 2 ],
        [ w - point, h ],
        [ 0, h ],
        [ point, h / 2 ],
      ] );
    },
  },
  {
    id: 'ribbon',
    ratio: 3.2,
    name: 'Ribbon',
    group: 'Arrows and lines',
    path: ( w, h ) => {
      const tail = Math.min( w * 0.14, h );
      const fold = h * 0.18;

      return ring( [
        [ 0, fold ],
        [ tail, fold ],
        [ tail, 0 ],
        [ w - tail, 0 ],
        [ w - tail, fold ],
        [ w, fold ],
        [ w - tail * 0.7, h / 2 ],
        [ w, h ],
        [ w - tail, h ],
        [ w - tail, h - fold ],
        [ tail, h - fold ],
        [ tail, h ],
        [ 0, h ],
        [ tail * 0.7, h / 2 ],
      ] );
    },
  },

  /* --- Callouts --------------------------------------------- */
  {
    id: 'bubble',
    ratio: 1.6,
    name: 'Speech bubble',
    group: 'Callouts',
    adjust: 'radius',
    path: ( w, h, l ) => {
      // The body stops short of the bottom; the tail uses what is left.
      const tail = Math.min( h * 0.22, 6 );
      const body = h - tail;
      const r = Math.max( 0, Math.min( l.radius || Math.min( w, body ) * 0.18, w / 2, body / 2 ) );
      const k = r * K;

      return [
        M( r, 0 ),
        L( w - r, 0 ),
        C( w - r + k, 0, w, r - k, w, r ),
        L( w, body - r ),
        C( w, body - r + k, w - r + k, body, w - r, body ),
        L( w * 0.34, body ),
        L( w * 0.22, h ),
        L( w * 0.2, body ),
        L( r, body ),
        C( r - k, body, 0, body - r + k, 0, body - r ),
        L( 0, r ),
        C( 0, r - k, r - k, 0, r, 0 ),
        'Z',
      ].join( ' ' );
    },
  },
  {
    id: 'round-callout',
    ratio: 1.6,
    name: 'Round callout',
    group: 'Callouts',
    path: ( w, h ) => {
      const tail = Math.min( h * 0.22, 6 );
      const body = h - tail;
      const cx = w / 2;
      const cy = body / 2;
      const rx = w / 2;
      const ry = body / 2;
      const ox = rx * K;
      const oy = ry * K;

      return [
        M( cx, 0 ),
        C( cx + ox, 0, w, cy - oy, w, cy ),
        C( w, cy + oy, cx + ox, body, cx, body ),
        // Out to the point and back, before the last quarter closes the oval.
        L( cx - w * 0.06, body ),
        L( cx - w * 0.16, h ),
        L( cx - w * 0.2, body * 0.97 ),
        C( cx - rx * 0.72, body * 0.9, 0, cy + oy, 0, cy ),
        C( 0, cy - oy, cx - ox, 0, cx, 0 ),
        'Z',
      ].join( ' ' );
    },
  },
  {
    id: 'thought',
    name: 'Thought bubble',
    group: 'Callouts',
    path: ( w, h ) => {
      const body = h * 0.74;

      return [
        ellipse( w / 2, body / 2, w / 2, body / 2 ),
        ellipse( w * 0.26, body + ( h - body ) * 0.34, w * 0.075, ( h - body ) * 0.3 ),
        ellipse( w * 0.16, body + ( h - body ) * 0.76, w * 0.045, ( h - body ) * 0.2 ),
      ].join( ' ' );
    },
  },

  /* --- Decorative ------------------------------------------- */
  {
    id: 'heart',
    name: 'Heart',
    group: 'Decorative',
    path: ( w, h ) => [
      M( w / 2, h ),
      C( w * 0.08, h * 0.62, 0, h * 0.38, 0, h * 0.26 ),
      C( 0, h * 0.06, w * 0.26, -h * 0.04, w / 2, h * 0.2 ),
      C( w * 0.74, -h * 0.04, w, h * 0.06, w, h * 0.26 ),
      C( w, h * 0.38, w * 0.92, h * 0.62, w / 2, h ),
      'Z',
    ].join( ' ' ),
  },
  {
    id: 'blob',
    name: 'Blob',
    group: 'Decorative',
    // Deliberately lopsided. A blob that is symmetrical is an ellipse with
    // extra steps, and reads as one at card size.
    path: ( w, h ) => [
      M( w * 0.46, 0 ),
      C( w * 0.86, h * 0.02, w * 1.02, h * 0.3, w * 0.94, h * 0.55 ),
      C( w * 0.86, h * 0.82, w * 0.6, h * 1.02, w * 0.36, h * 0.96 ),
      C( w * 0.12, h * 0.9, -w * 0.04, h * 0.62, w * 0.02, h * 0.36 ),
      C( w * 0.08, h * 0.12, w * 0.24, -h * 0.02, w * 0.46, 0 ),
      'Z',
    ].join( ' ' ),
  },
  {
    id: 'cross',
    name: 'Cross',
    group: 'Decorative',
    path: ( w, h ) => {
      const arm = Math.min( w, h ) * 0.3;

      return ring( [
        [ w / 2 - arm / 2, 0 ],
        [ w / 2 + arm / 2, 0 ],
        [ w / 2 + arm / 2, h / 2 - arm / 2 ],
        [ w, h / 2 - arm / 2 ],
        [ w, h / 2 + arm / 2 ],
        [ w / 2 + arm / 2, h / 2 + arm / 2 ],
        [ w / 2 + arm / 2, h ],
        [ w / 2 - arm / 2, h ],
        [ w / 2 - arm / 2, h / 2 + arm / 2 ],
        [ 0, h / 2 + arm / 2 ],
        [ 0, h / 2 - arm / 2 ],
        [ w / 2 - arm / 2, h / 2 - arm / 2 ],
      ] );
    },
  },
  {
    id: 'frame',
    name: 'Frame',
    group: 'Decorative',
    adjust: 'radius',
    path: ( w, h, l ) => {
      // Two rings, the inner one reversed, so the middle is a hole rather than
      // a second solid. Even-odd or nonzero, both read this the same way.
      const t = Math.max( 0.5, Math.min( w, h ) * 0.12 );
      const outer = rounded( w, h, l.radius );
      const inner = rounded( w - t * 2, h - t * 2, Math.max( 0, l.radius - t ) );

      return `${ outer } ${ shift( reverse( inner ), t, t ) }`;
    },
  },
  {
    id: 'semicircle',
    ratio: 2,
    name: 'Half circle',
    group: 'Decorative',
    path: ( w, h ) => {
      const k = ( w / 2 ) * K;

      return [
        M( 0, h ),
        C( 0, h - h * K, w / 2 - k, 0, w / 2, 0 ),
        C( w / 2 + k, 0, w, h - h * K, w, h ),
        'Z',
      ].join( ' ' );
    },
  },
  {
    id: 'free',
    name: 'Drawn',
    group: 'Decorative',
    /*
     * Not offered in the picker: a drawn path has no shape until somebody has
     * drawn it, so there is nothing to put on a tile. It is reached with the
     * pen, and it is listed here so that the shape menu can still name it when
     * one is selected.
     */
    drawn: true,
    path: ( w, h, l ) => freePath( l.nodes ?? [], !! l.closed, w, h ),
  },
  {
    id: 'quarter',
    name: 'Quarter circle',
    group: 'Decorative',
    path: ( w, h ) => [
      M( 0, h ),
      L( 0, 0 ),
      C( w * K, 0, w, h - h * K, w, h ),
      'Z',
    ].join( ' ' ),
  },
];

/**
 * A drawn path, from its points.
 *
 * The points are proportions of the box, so this is where a drawing becomes
 * millimetres - or pixels, or tile units, depending on who asked. A segment is
 * a curve when either end offers a handle and a straight line when neither
 * does, which is what makes a corner a corner without a flag saying so.
 */
export function freePath( nodes: PathNode[], closed: boolean, w: number, h: number ): string {
  if ( nodes.length < 2 ) return '';

  const at = ( n: PathNode ) => [ n.x * w, n.y * h ] as const;

  const out: string[] = [];
  const first = nodes[ 0 ] as PathNode;
  const [ fx, fy ] = at( first );

  out.push( M( fx, fy ) );

  const segment = ( from: PathNode, to: PathNode ) => {
    const [ tx, ty ] = at( to );

    if ( ! from.out && ! to.in ) {
      out.push( L( tx, ty ) );

      return;
    }

    // A handle that is not there sits on its own anchor, which turns the cubic
    // into the straight line it should be on that side.
    const [ ax, ay ] = at( from );
    const c1 = from.out ? [ from.out.x * w, from.out.y * h ] : [ ax, ay ];
    const c2 = to.in ? [ to.in.x * w, to.in.y * h ] : [ tx, ty ];

    out.push( C( c1[ 0 ] as number, c1[ 1 ] as number, c2[ 0 ] as number, c2[ 1 ] as number, tx, ty ) );
  };

  for ( let i = 1; i < nodes.length; i++ ) {
    segment( nodes[ i - 1 ] as PathNode, nodes[ i ] as PathNode );
  }

  if ( closed ) {
    segment( nodes[ nodes.length - 1 ] as PathNode, first );
    out.push( 'Z' );
  }

  return out.join( ' ' );
}

/* ---------------------------------------------------------------
 * Path surgery, for the shapes that need a hole
 * ------------------------------------------------------------ */

/**
 * Reverses a closed path so that it cuts a hole rather than filling.
 *
 * Only handles the M / L / C / Z that this file produces - it is not a general
 * SVG path parser, and does not need to be.
 */
function reverse( d: string ): string {
  const tokens = d.match( /[MLCZ][^MLCZ]*/gi ) ?? [];
  const points: Array<{ kind: string; nums: number[] }> = tokens.map( ( t ) => ( {
    kind: t[ 0 ]?.toUpperCase() ?? 'M',
    nums: ( t.slice( 1 ).match( /-?[\d.]+/g ) ?? [] ).map( Number ),
  } ) );

  const out: string[] = [];
  const at = ( i: number ) => {
    const n = points[ i ]?.nums ?? [];

    return { x: n[ n.length - 2 ] ?? 0, y: n[ n.length - 1 ] ?? 0 };
  };

  const last = points.filter( ( p ) => p.kind !== 'Z' ).length - 1;
  const end = at( last );

  out.push( M( end.x, end.y ) );

  for ( let i = last; i > 0; i-- ) {
    const step = points[ i ];
    const before = at( i - 1 );

    if ( ! step ) continue;

    if ( step.kind === 'C' ) {
      // The control points swap when the direction does.
      out.push( C( step.nums[ 2 ] ?? 0, step.nums[ 3 ] ?? 0, step.nums[ 0 ] ?? 0, step.nums[ 1 ] ?? 0, before.x, before.y ) );
    } else {
      out.push( L( before.x, before.y ) );
    }
  }

  out.push( 'Z' );

  return out.join( ' ' );
}

/** Moves a path, for placing an inner ring inside an outer one. */
function shift( d: string, dx: number, dy: number ): string {
  return d.replace( /([MLC])([^MLCZ]*)/gi, ( _all, kind: string, rest: string ) => {
    const nums = ( rest.match( /-?[\d.]+/g ) ?? [] ).map( Number );
    const moved = nums.map( ( n, i ) => mm( n + ( i % 2 === 0 ? dx : dy ) ) );

    return `${ kind } ${ moved.join( ' ' ) } `;
  } );
}

/* ---------------------------------------------------------------
 * Lookup
 * ------------------------------------------------------------ */

const BY_ID = new Map( SHAPES.map( ( s ) => [ s.id, s ] ) );

export function shapeDef( id: string ): ShapeDef {
  // Anything unknown draws as a rectangle rather than as nothing. A document
  // from a newer version of the editor should lose the shape, not the layer.
  return BY_ID.get( id as ShapeLayer['shape'] ) ?? ( SHAPES[ 0 ] as ShapeDef );
}

/** The path for a layer, in millimetres, relative to its own top-left. */
export function shapePath( layer: ShapeLayer ): string {
  return shapeDef( layer.shape ).path( layer.w, layer.h, layer );
}

/** The catalogue in display order, grouped for the picker. */
export function shapeGroups(): Array<{ group: ShapeDef['group']; shapes: ShapeDef[] }> {
  const order: ShapeDef['group'][] = [
    'Basic', 'Polygons', 'Stars and badges', 'Arrows and lines', 'Callouts', 'Decorative',
  ];

  return order.map( ( group ) => ( {
    group,
    // A drawn path has nothing to preview until it exists, so it is not offered
    // here - the pen is how one is made.
    shapes: SHAPES.filter( ( s ) => s.group === group && ! s.drawn ),
  } ) );
}
